import { prefixes, useTypedTranslation } from '@/locales';
import { Input } from '@/components/_untitled/base/input/input';
import { getPasswordChecks, type PasswordChecks } from '../utils/passwordValidation';

interface PasswordFieldsProps {
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  autoFocus?: boolean;
}

function PasswordStrengthIndicators({ checks }: { checks: PasswordChecks }) {
  const [tIndicators] = useTypedTranslation(prefixes('kyc.steps.account.indicators'));
  const indicators = [
    { key: 'length', label: tIndicators('length'), passed: checks.length },
    { key: 'number', label: tIndicators('number'), passed: checks.number },
    { key: 'uppercase', label: tIndicators('uppercase'), passed: checks.uppercase },
    { key: 'symbol', label: tIndicators('symbol'), passed: checks.symbol },
  ];

  return (
    <div className="flex gap-4 px-2">
      {indicators.map((indicator) => (
        <div key={indicator.key} className="flex-1 flex flex-col gap-1">
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${
                indicator.passed ? 'bg-gray-900 w-full' : 'w-0'
              }`}
            />
          </div>
          <p className="text-[10px] font-normal text-gray-400 text-center uppercase leading-[18px]">
            {indicator.label}
          </p>
        </div>
      ))}
    </div>
  );
}

export function PasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  autoFocus = false,
}: PasswordFieldsProps) {
  const [tAccount] = useTypedTranslation(prefixes('kyc.steps.account'));
  const passwordChecks = getPasswordChecks(password);

  return (
    <>
      <Input
        type="password"
        label={ tAccount('password') }
        value={password}
        onChange={onPasswordChange}
        aria-label={ tAccount('password') }
        isRequired
        autoFocus={autoFocus}
        data-testid="password-input"
      />

      <PasswordStrengthIndicators checks={passwordChecks} />

      <Input
        type="password"
        label={ tAccount('confirmPassword') }
        value={confirmPassword}
        onChange={onConfirmPasswordChange}
        aria-label={ tAccount('confirmPassword') }
        isRequired
        data-testid="confirm-password-input"
      />
    </>
  );
}
